const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
const letters = (text) => segmenter ? Array.from(segmenter.segment(text), (part) => part.segment) : Array.from(text);

export function compactTitle(full, fits) {
  if (fits(full)) return full;
  const year = full.match(/\s*\(\d{4}\)$/u)?.[0].trim() || '';
  const name = year ? full.slice(0, full.lastIndexOf(year)).trimEnd() : full;
  const chars = letters(name);
  const lastWord = name.match(/\S+$/u)?.[0] || name;
  let tail = letters(lastWord);
  const end = () => `${tail.join('')}${year ? ` ${year}` : ''}`.trim();
  while (tail.length > (year ? 0 : 1) && !fits(`${chars.slice(0, 3).join('')}… ${end()}`)) tail.shift();
  let low = 0;
  let high = Math.max(0, chars.length - tail.length - 1);
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (fits(`${chars.slice(0, middle).join('').trimEnd()}… ${end()}`)) low = middle;
    else high = middle - 1;
  }
  let prefix = chars.slice(0, low).join('').trimEnd();
  const boundary = prefix.lastIndexOf(' ');
  if (boundary >= 4 && prefix.length - boundary <= 4) prefix = prefix.slice(0, boundary);
  return `${prefix}… ${end()}`;
}

export function fitsLines(text, width, lines, measure) {
  if (lines === 1) return measure(text) <= width;
  let count = 1;
  let line = '';
  for (const word of text.split(/\s+/u)) {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate) <= width) {
      line = candidate;
      continue;
    }
    if (line) {
      if (++count > lines) return false;
      line = '';
    }
    for (const char of letters(word)) {
      if (measure(char) > width) return false;
      if (line && measure(line + char) > width) {
        if (++count > lines) return false;
        line = '';
      }
      line += char;
    }
  }
  return true;
}

if (typeof document !== 'undefined' && typeof ResizeObserver !== 'undefined') {
  const selector = '.product-card .product-title > p, .product-card .product-grid-view-zoom-out--details > .h4, .resource-card[data-resource-type="product"] .resource-card__title';
  const titles = new Map();
  const pending = new Set();
  const context = document.createElement('canvas').getContext('2d');
  let frame;

  const flush = () => {
    frame = 0;
    for (const element of pending) {
      const state = titles.get(element);
      if (!state || !element.isConnected) continue;
      const width = element.clientWidth - 1;
      if (width <= 0 || !context) continue;
      const style = getComputedStyle(element);
      context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const spacing = Number.parseFloat(style.letterSpacing) || 0;
      const measure = (text) => context.measureText(text).width + Math.max(0, letters(text).length - 1) * spacing;
      const lines = width < 200 ? 2 : 1;
      element.dataset.titleLines = String(lines);
      const visible = compactTitle(state.full, (text) => fitsLines(text, width, lines, measure));
      if (state.visible.textContent !== visible) state.visible.textContent = visible;
      element.title = visible === state.full ? '' : state.full;
      const link = element.closest('.product-card')?.querySelector('.product-card__link') || element.closest('a.resource-card');
      if (link) link.title = element.title;
    }
    pending.clear();
    for (const element of titles.keys()) {
      if (element.isConnected) continue;
      resize.unobserve(element);
      titles.delete(element);
    }
  };

  const queue = (element) => {
    pending.add(element);
    if (!frame) frame = requestAnimationFrame(flush);
  };

  const resize = new ResizeObserver((entries) => entries.forEach(({ target }) => queue(target)));
  const enhance = (element) => {
    if (titles.has(element)) return;
    const full = element.textContent.trim();
    if (!full) return;
    const accessible = document.createElement('span');
    accessible.className = 'visually-hidden';
    accessible.textContent = full;
    const visible = document.createElement('span');
    visible.className = 'sm-card-title__display';
    visible.setAttribute('aria-hidden', 'true');
    visible.textContent = full;
    titles.set(element, { full, visible });
    element.replaceChildren(accessible, visible);
    resize.observe(element);
    queue(element);
  };

  const scan = (root) => {
    if (!(root instanceof Element)) return;
    if (root.matches(selector)) enhance(root);
    root.querySelectorAll(selector).forEach(enhance);
  };

  scan(document.body);
  new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach(scan);
  }).observe(document.body, { childList: true, subtree: true });
  document.fonts.ready.then(() => titles.forEach((state, element) => queue(element)));
}
